import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Settings, Clock, History, Send, CheckCircle, XCircle, Mail, MessageSquare, Calendar, ChevronDown, Save, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const RappelsPage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('upcoming');
  const [config, setConfig] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [logs, setLogs] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logFilter, setLogFilter] = useState('');
  const [schedulerJobs, setSchedulerJobs] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, upRes, logRes, svcRes, schedRes] = await Promise.all([
        axios.get(`${API}/reminder-config`),
        axios.get(`${API}/upcoming-reminders`),
        axios.get(`${API}/notification-logs?limit=80`),
        axios.get(`${API}/reminders/history?limit=50`),
        axios.get(`${API}/scheduler-status`),
      ]);
      setConfig(cfgRes.data);
      setUpcoming(upRes.data.reminders || []);
      setLogs(logRes.data);
      setServiceHistory(svcRes.data);
      setSchedulerJobs(schedRes.data.jobs || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/reminder-config`, config);
      setConfig(res.data);
      toast.success('Configuration sauvegardee');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };

  const fmtChannel = (ch) => {
    if (ch === 'whatsapp') return 'WhatsApp';
    if (ch === 'email') return 'Email';
    if (ch === 'both') return 'WhatsApp + Email';
    return ch || '—';
  };

  const timeUntil = (isoStr) => {
    if (!isoStr) return '—';
    const diff = new Date(isoStr) - new Date();
    if (diff < 0) return 'imminent';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (hrs < 24) return `${hrs}h${remMins > 0 ? String(remMins).padStart(2,'0') : ''}`;
    const days = Math.floor(hrs / 24);
    return `${days}j ${hrs % 24}h`;
  };

  const filteredLogs = logFilter ? [...logs, ...serviceHistory.map(s => ({...s, type: 'service_reminder'}))].filter(l => l.type === logFilter) : [...logs, ...serviceHistory.map(s => ({...s, type: 'service_reminder'}))];
  const sortedLogs = filteredLogs.sort((a, b) => (b.created_at || b.sent_at || '').localeCompare(a.created_at || a.sent_at || ''));

  if (loading) return <div className="p-6 text-center text-slate-400">{t('common.loading')}</div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="rappels-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-blue-600" /> Rappels automatiques
        </h1>
        <Button size="sm" variant="outline" onClick={loadAll} data-testid="refresh-rappels">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
        {[
          { id: 'upcoming', label: 'A venir', icon: Calendar, count: upcoming.length },
          { id: 'config', label: 'Configuration', icon: Settings },
          { id: 'history', label: 'Historique', icon: History, count: sortedLogs.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
            {t.count !== undefined && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ===== UPCOMING TAB ===== */}
      {tab === 'upcoming' && (
        <div className="space-y-3" data-testid="upcoming-reminders">
          {upcoming.length === 0 ? (
            <Card className="p-8 text-center">
              <Calendar className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">Aucun rappel planifie pour les 7 prochains jours</p>
            </Card>
          ) : (
            upcoming.map(r => (
              <Card key={r.id} className="p-4" data-testid={`reminder-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${r.reminder_sent ? 'bg-green-100' : r.has_custom_reminder ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {r.reminder_sent ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Bell className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{r.client_name}</p>
                      <p className="text-xs text-slate-500">{r.vehicle_info || r.description || r.category}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {r.date} {r.time}
                        </span>
                        <span className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          {r.channel === 'whatsapp' ? <MessageSquare className="w-3 h-3" /> : r.channel === 'email' ? <Mail className="w-3 h-3" /> : <><MessageSquare className="w-3 h-3" /><Mail className="w-3 h-3" /></>}
                          {fmtChannel(r.channel)}
                        </span>
                        <span className="text-[10px] bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {r.delay} avant
                        </span>
                        {r.has_custom_reminder && (
                          <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">Personnalise</span>
                        )}
                      </div>
                      {r.custom_message && <p className="text-xs text-slate-400 mt-1 italic">"{r.custom_message}"</p>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {r.reminder_sent ? (
                      <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">Envoye</span>
                    ) : (
                      <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">{t('common.pending')}</span>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ===== CONFIG TAB ===== */}
      {tab === 'config' && config && (
        <div className="space-y-4" data-testid="reminder-config">
          <Card className="p-5 space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" /> Rappels RDV
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Heure d'envoi</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} max={23} className="w-16" value={config.appointment_reminder_hour ?? 17}
                    onChange={e => setConfig(c => ({...c, appointment_reminder_hour: parseInt(e.target.value) || 0}))}
                    data-testid="config-appt-hour" />
                  <span className="self-center text-slate-400">:</span>
                  <Input type="number" min={0} max={59} className="w-16" value={config.appointment_reminder_minute ?? 0}
                    onChange={e => setConfig(c => ({...c, appointment_reminder_minute: parseInt(e.target.value) || 0}))}
                    data-testid="config-appt-minute" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Delai avant RDV</Label>
                <Select value={String(config.appointment_delay_hours ?? 24)} onValueChange={v => setConfig(c => ({...c, appointment_delay_hours: parseInt(v)}))}>
                  <SelectTrigger data-testid="config-appt-delay"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 heure</SelectItem>
                    <SelectItem value="2">2 heures</SelectItem>
                    <SelectItem value="12">12 heures</SelectItem>
                    <SelectItem value="24">24 heures (J-1)</SelectItem>
                    <SelectItem value="48">48 heures (J-2)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Canal par defaut</Label>
                <Select value={config.default_channel || 'both'} onValueChange={v => setConfig(c => ({...c, default_channel: v}))}>
                  <SelectTrigger data-testid="config-default-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="both">WhatsApp + Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Settings className="w-4 h-4 text-orange-500" /> Rappels service / entretien
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Heure d'envoi</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} max={23} className="w-16" value={config.service_reminder_hour ?? 7}
                    onChange={e => setConfig(c => ({...c, service_reminder_hour: parseInt(e.target.value) || 0}))}
                    data-testid="config-service-hour" />
                  <span className="self-center text-slate-400">:</span>
                  <Input type="number" min={0} max={59} className="w-16" value={config.service_reminder_minute ?? 0}
                    onChange={e => setConfig(c => ({...c, service_reminder_minute: parseInt(e.target.value) || 0}))}
                    data-testid="config-service-minute" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Seuil (mois sans visite)</Label>
                <Input type="number" min={1} max={24} value={config.service_months_threshold ?? 6}
                  onChange={e => setConfig(c => ({...c, service_months_threshold: parseInt(e.target.value) || 6}))}
                  data-testid="config-service-months" />
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-green-600" /> Autres rappels
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Rappels pneus - Heure</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} max={23} className="w-16" value={config.tire_reminder_hour ?? 7}
                    onChange={e => setConfig(c => ({...c, tire_reminder_hour: parseInt(e.target.value) || 0}))}
                    data-testid="config-tire-hour" />
                  <span className="self-center text-slate-400">:</span>
                  <Input type="number" min={0} max={59} className="w-16" value={config.tire_reminder_minute ?? 0}
                    onChange={e => setConfig(c => ({...c, tire_reminder_minute: parseInt(e.target.value) || 0}))}
                    data-testid="config-tire-minute" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Enquete satisfaction - Heure</Label>
                <div className="flex gap-1">
                  <Input type="number" min={0} max={23} className="w-16" value={config.satisfaction_hour ?? 10}
                    onChange={e => setConfig(c => ({...c, satisfaction_hour: parseInt(e.target.value) || 0}))}
                    data-testid="config-satisfaction-hour" />
                  <span className="self-center text-slate-400">:</span>
                  <Input type="number" min={0} max={59} className="w-16" value={config.satisfaction_minute ?? 0}
                    onChange={e => setConfig(c => ({...c, satisfaction_minute: parseInt(e.target.value) || 0}))}
                    data-testid="config-satisfaction-minute" />
                </div>
              </div>
            </div>
          </Card>

          <Button className="w-full" onClick={saveConfig} disabled={saving} data-testid="save-config-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement...' : 'Enregistrer la configuration'}
          </Button>

          {/* Scheduler next runs */}
          {schedulerJobs.length > 0 && (
            <Card className="p-5 space-y-3" data-testid="scheduler-status">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" /> Prochaines executions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {schedulerJobs.map(job => (
                  <div key={job.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2" data-testid={`job-${job.id}`}>
                    <span className="text-xs text-slate-600 dark:text-slate-300">{job.label}</span>
                    <div className="text-right">
                      <span className="text-xs font-mono text-indigo-600 dark:text-indigo-400">{job.next_run_human}</span>
                      <span className="text-[10px] text-slate-400 ml-1.5">({timeUntil(job.next_run)})</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {tab === 'history' && (
        <div className="space-y-3" data-testid="notification-history">
          <div className="flex gap-2">
            <Select value={logFilter} onValueChange={setLogFilter}>
              <SelectTrigger className="w-48" data-testid="filter-type"><SelectValue placeholder="Tous les types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">Tous les types</SelectItem>
                <SelectItem value="appointment_reminder">Rappels RDV</SelectItem>
                <SelectItem value="vehicle_ready">Vehicule pret</SelectItem>
                <SelectItem value="send_quote">Envoi devis</SelectItem>
                <SelectItem value="send_invoice">Envoi facture</SelectItem>
                <SelectItem value="service_reminder">Rappel service</SelectItem>
              </SelectContent>
            </Select>
            <p className="self-center text-xs text-slate-400">{sortedLogs.length} notification(s)</p>
          </div>

          {sortedLogs.length === 0 ? (
            <Card className="p-8 text-center">
              <History className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">Aucune notification envoyee</p>
            </Card>
          ) : (
            sortedLogs.slice(0, 50).map((log, idx) => {
              const isSuccess = log.success !== false && !log.results?.email?.error && !log.results?.whatsapp?.error;
              const typeLabels = {
                appointment_reminder: 'Rappel RDV',
                vehicle_ready: 'Vehicule pret',
                send_quote: 'Envoi devis',
                send_invoice: 'Envoi facture',
                service_reminder: 'Rappel service',
              };
              return (
                <Card key={log.id || idx} className="p-3" data-testid={`log-${idx}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                      {isSuccess ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{log.client_name || '—'}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          {typeLabels[log.type] || log.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                        <Clock className="w-3 h-3" /> {fmtDate(log.created_at || log.sent_at)}
                        {log.channel && <><span className="mx-1">·</span>{fmtChannel(log.channel)}</>}
                        {log.preference && <><span className="mx-1">·</span>{fmtChannel(log.preference)}</>}
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${isSuccess ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                      {isSuccess ? 'OK' : 'Echec'}
                    </span>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default RappelsPage;
